/**
 * Save-button state controller.
 *
 * Replaces the previous practice of assembling the save button's
 * appearance independently at 5 sites — `showEmptyState`,
 * `showWorkspace`, `updateSaveBlockUI`, the click handler (Saving),
 * the WASM-fallback path (Exporting). Each site flipped some subset
 * of `disabled`, `textContent`, `classList`, `title`, and
 * `aria-label`, leading to drift (e.g., a successful-save finally
 * branch removed `is-saving` but did NOT re-evaluate
 * `is-save-blocked`, so a save that resolved while pending inserts
 * had been added would land with a normal-styled button hiding the
 * block).
 *
 * One function (`setSaveBtnState`) owns the full attribute set as a
 * unit. Each enum value maps to a deterministic
 * (label, classes, disabled, title, aria-label) tuple.
 */
export const SaveBtnState = Object.freeze({
    EMPTY: "empty", // no PDF loaded; button disabled, "Save to Cloud" label
    BLOCKED: "blocked", // pending UNDEFINED inserts; amber tint
    SAVING: "saving", // explicit Save-to-Cloud in flight (button disabled)
    // AUTOSAVING: background auto-save flush in flight. NON-BLOCKING — the
    // button stays ENABLED and the canvas stays live so the user keeps
    // editing; only the label + aria-live announce the transient state. This
    // is the load-bearing difference from SAVING (which is an explicit,
    // button-modal save): autosave must never lock the editor.
    AUTOSAVING: "autosaving",
    EXPORTING: "exporting", // download serialize/fetch in flight (button disabled)
    READY: "ready", // PDF loaded, no blocks, idle
});
export function setSaveBtnState(btn, target, opts = {}) {
    if (!btn)
        return;
    // Reset all the variant classes; we'll re-add only the one matching
    // the target. This is the regression-preventing property: a stale
    // class from a prior state (is-saving + is-save-blocked) cannot
    // outlive the transition.
    btn.classList.remove("is-saving", "is-save-blocked");
    switch (target) {
        case SaveBtnState.EMPTY:
            btn.disabled = true;
            btn.textContent = "Save to Cloud";
            btn.title = "Save to Cloud (Ctrl+S)";
            btn.setAttribute("aria-label", "Save to Cloud (Ctrl+S)");
            break;
        case SaveBtnState.READY:
            btn.disabled = false;
            btn.textContent = "Save to Cloud";
            btn.title = "Save to Cloud (Ctrl+S)";
            btn.setAttribute("aria-label", "Save to Cloud (Ctrl+S)");
            break;
        case SaveBtnState.BLOCKED: {
            btn.disabled = false;
            btn.textContent = "Save to Cloud";
            btn.classList.add("is-save-blocked");
            const msg = opts.blockMessage ?? "Some elements need a type before saving.";
            btn.title = msg;
            btn.setAttribute("aria-label", `Save to Cloud blocked: ${msg}`);
            break;
        }
        case SaveBtnState.SAVING:
            btn.disabled = true;
            btn.textContent = "Saving…";
            btn.classList.add("is-saving");
            btn.title = "Saving to cloud";
            btn.setAttribute("aria-label", "Saving to cloud");
            break;
        case SaveBtnState.AUTOSAVING:
            // NON-BLOCKING: leave the button ENABLED (the user must be able to
            // keep editing / trigger an explicit save while autosave runs). The
            // aria-live="polite" attribute on the button element itself carries
            // the announcement to screen-reader users.
            btn.disabled = false;
            btn.textContent = "Autosaving…";
            btn.classList.add("is-saving");
            btn.title = "Autosaving to cloud (you can keep editing)";
            btn.setAttribute("aria-label", "Autosaving to cloud — you can keep editing");
            break;
        case SaveBtnState.EXPORTING:
            btn.disabled = true;
            btn.textContent = "Downloading…";
            btn.classList.add("is-saving");
            btn.title = "Preparing download";
            btn.setAttribute("aria-label", "Preparing download");
            break;
        default:
            throw new Error(`[save-btn-state] unknown target: ${target}`);
    }
}
export function readSaveBtnState(btn) {
    return {
        disabled: !!(btn?.disabled),
        label: btn ? btn.textContent : "",
        classes: btn ? Array.from(btn.classList) : [],
        title: btn ? btn.title : "",
        ariaLabel: btn ? (btn.getAttribute("aria-label") ?? "") : "",
    };
}
